import React, { useState, useEffect } from "react";
import { useNavigate, useLocation } from "react-router-dom";
import { useAppDispatch } from "../../../redux/store";
import {
  fetchAddProfile,
  fetchUpdateProfile,
  fetchMyProfiles,
} from "../../../redux/profile/profileInfo/asyncActions";
import { Button } from "../../../components";
import { IMyprofile } from "../../../redux/profile/profileInfo/types";
import { useLookUpUsers } from "../../../hooks/useLookUpUsers";
import { dateMask } from "../../../utils/form/dateMask";
import { formatDateToISO } from "../../../helpers/dateFormatter";
import styles from "./EditProfileScreen.module.scss";

interface LocationState {
  title?: string;
  profile?: IMyprofile;
  edit?: boolean;
  profileType?: "adult" | "child";
}

export const EditProfileScreen: React.FC = () => {
  const navigate = useNavigate();
  const dispatch = useAppDispatch();
  const location = useLocation();
  const { getBitrixUser } = useLookUpUsers();

  const locationState = location.state as LocationState | null;
  const { profile, edit, profileType: routeProfileType } = locationState || {};

  const [profileType, setProfileType] = useState<"adult" | "child">(() => {
    if (edit && profile) {
      return profile.is_adult ? "adult" : "child";
    } else if (routeProfileType) {
      return routeProfileType === "child" ? "child" : "adult";
    }
    return "adult";
  });

  const [profileName, setProfileName] = useState(edit && profile ? profile.name : "");
  const [profilePhone, setProfilePhone] = useState(
    edit && profile ? profile.phone || "" : ""
  );
  const [profileDate, setProfileDate] = useState(
    edit && profile && profile.birth_date
      ? profile.birth_date.split("-").reverse().join(".")
      : ""
  );
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");

  const deletePhoneSymbol = (phone: string): string => {
    return phone.replace(/\D/g, "");
  };

  const isDisabled = edit
    ? (profileType === "adult" &&
        profile?.name === profileName &&
        deletePhoneSymbol(profile?.phone || "") === deletePhoneSymbol(profilePhone)) ||
      (profileType === "child" &&
        profile?.name === profileName &&
        profile?.birth_date === profileDate.split(".").reverse().join("-"))
    : (profileType === "adult" && (!profileName || !profilePhone)) ||
      (profileType === "child" && (!profileName || !profileDate));

  const handleProfileTypeChange = (type: "adult" | "child") => {
    if (edit) return;
    setProfileType(type);
    setError("");
  };


  const handleSaveProfile = async () => {
    if (isDisabled) return;

    setLoading(true);
    setError("");

    try {
      const phoneTemp = profileType === "adult" ? `+${profilePhone.replace(/\D/g, "")}` : "";
      const formattedDate = profileType === "child" ? formatDateToISO(profileDate) : "";

      if (edit && profile) {
        const updateData: any = {
          name: profileName,
          is_adult: profileType === "adult",
        };

        if (profileType === "adult") {
          updateData.phone = phoneTemp;
          updateData.birth_date = null;
        } else {
          updateData.birth_date = formattedDate;
          updateData.phone = null;
        }

        const result = await dispatch(
          fetchUpdateProfile({ profileId: profile.id, data: updateData })
        );

        if (result.payload === 200) {
          await dispatch(fetchMyProfiles());
          navigate(-1);
        } else {
          setError("Ошибка при обновлении профиля");
        }
      } else {
        let bitrixPartnerId: string | number | null = null;

        try {
          if (profileType === "adult" && phoneTemp) {
            bitrixPartnerId = await getBitrixUser(phoneTemp, null, null, null, true);
          } else if (profileType === "child" && formattedDate) {
            bitrixPartnerId = await getBitrixUser(
              null,
              null,
              formattedDate,
              profileName,
              true
            );
          }
        } catch (error) {
        }

        const createData: any = {
          name: profileName,
          is_adult: profileType === "adult",
        };

        if (profileType === "adult") {
          createData.phone = phoneTemp;
        } else {
          createData.birth_date = formattedDate;
        }

        if (bitrixPartnerId) {
          createData.partner_id = String(bitrixPartnerId);
        }

        const result = await dispatch(fetchAddProfile(createData));

        if (result.payload === 201) {
          await dispatch(fetchMyProfiles());
          navigate(-1);
        } else if (result.payload === 422) {
          setError("Профиль с такими данными уже существует");
        } else {
          setError("Ошибка при создании профиля");
        }
      }
    } catch (err) {
      setError("Ошибка при сохранении профиля");
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className={styles.container}>
      {!edit && (
        <div className={styles.profileTypeContainer}>
          <button
            className={`${styles.switchButton} ${
              profileType === "adult" ? styles.switchButtonActive : ""
            }`}
            onClick={() => handleProfileTypeChange("adult")}
          >
            Взрослый
          </button>
          <button
            className={`${styles.switchButton} ${
              profileType === "child" ? styles.switchButtonActive : ""
            }`}
            onClick={() => handleProfileTypeChange("child")}
          >
            Детский
          </button>
        </div>
      )}

      <div className={styles.profileInfoContainer}>
        {/* Поле ФИО */}
        <div className={styles.inputGroup}>
          <label className={styles.label}>ФИО</label>
          <input
            type="text"
            className={styles.input}
            value={profileName}
            onChange={(e) => {
              setProfileName(e.target.value);
              setError("");
            }}
            placeholder="Иванов Иван Иванович"
          />
        </div>

        {profileType === "adult" && (
          <div className={styles.inputGroup}>
            <label className={styles.label}>Телефон</label>
            <input
              type="tel"
              className={styles.input}
              value={profilePhone}
              onChange={(e) => {
                setProfilePhone(e.target.value);
                setError("");
              }}
              placeholder="+7 (___) ___-__-__"
            />
          </div>
        )}

        {profileType === "child" && (
          <div className={styles.inputGroup}>
            <label className={styles.label}>Дата рождения</label>
            <input
              type="text"
              className={styles.input}
              value={profileDate}
              onChange={(e) => {
                const maskedValue = dateMask(e);
                setProfileDate(maskedValue);
                setError("");
              }}
              placeholder="ДД.ММ.ГГГГ"
              maxLength={10}
            />
          </div>
        )}

        {error && <div className={styles.error}>{error}</div>}
      </div>

      <div className={styles.buttonContainer}>
        <Button
          onClickFunc={handleSaveProfile}
          newStyles={
            isDisabled || loading ? styles.disabledButton : styles.saveButton
          }
        >
          {loading ? "Сохранение..." : "Сохранить"}
        </Button>
      </div>
    </div>
  );
};

